import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { generateRandomBuild, getStarters, getRelics, getTier3Items } from '../utils/dataLoader';
import GodCard from '../components/GodCard';
import BuildDisplay from '../components/BuildDisplay';
import SocialShare from '../components/SocialShare';
import CounterStats from '../components/CounterStats';
import { incrementGodRolls, incrementBuildRolls } from '../utils/statsCounter';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
`;

const Header = styled.header`
  margin-bottom: 3rem;
  position: relative;
`;

const HeroSection = styled.div`
  background-color: var(--card-background);
  background-image: linear-gradient(to bottom, rgba(var(--primary-color-rgb), 0.05), rgba(var(--background-color-rgb), 0.05));
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 0;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, var(--primary-color), var(--secondary-color), var(--accent-color));
  }
`;

const HeroHeader = styled.div`
  padding: 1.2rem 2rem 0.8rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 15%;
    right: 15%;
    height: 1px;
    background: linear-gradient(to right, 
      transparent, 
      rgba(var(--primary-color-rgb), 0.2) 20%, 
      rgba(var(--primary-color-rgb), 0.2) 80%, 
      transparent
    );
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0;
  gap: 1px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroColumn = styled.div`
  padding: 1.25rem 2rem;
  
  &:first-of-type {
    border-right: 1px solid rgba(var(--primary-color-rgb), 0.1);
  }
  
  @media (max-width: 1024px) {
    &:first-of-type {
      border-right: none;
      border-bottom: 1px solid rgba(var(--primary-color-rgb), 0.1);
    }
  }
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 10px rgba(var(--primary-color-rgb), 0.5);
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: var(--text-color);
  opacity: 0.9;
  line-height: 1.5;
  margin: 0 auto;
  max-width: 800px;
`;

const AboutSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: var(--secondary-color);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    width: 18px;
    height: 18px;
    color: var(--accent-color);
  }
`;

const SectionContent = styled.div`
  background-color: rgba(var(--primary-color-rgb), 0.05);
  border-radius: var(--border-radius);
  padding: 1rem;
  
  p {
    margin: 0;
    line-height: 1.4;
    font-size: 0.95rem;
  }
`;

const HowToSection = styled.div``;

const StepsList = styled.ol`
  list-style: none;
  counter-reset: step-counter;
  padding: 0;
  margin: 0;
`;

const Step = styled.li`
  counter-increment: step-counter;
  margin-bottom: 0.75rem;
  padding-left: 1.75rem;
  position: relative;
  line-height: 1.4;
  font-size: 0.95rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &::before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: 0;
    background-color: var(--accent-color);
    color: var(--background-color);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.8rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin: 0 auto;
  padding: 1.5rem;
  max-width: 600px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 15%;
    right: 15%;
    height: 1px;
    background: linear-gradient(to right, 
      transparent, 
      rgba(var(--primary-color-rgb), 0.2) 20%, 
      rgba(var(--primary-color-rgb), 0.2) 80%, 
      transparent
    );
  }
`;

const GenerateButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-size: 1.2rem;
  padding: 1rem 2rem;
  border-radius: 50px;
  box-shadow: 0 4px 15px rgba(var(--primary-color-rgb), 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(var(--primary-color-rgb), 0.5);
    background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

const NewBuildButton = styled(motion.button)`
  background: transparent;
  border: 2px solid var(--secondary-color);
  color: var(--secondary-color);
  border-radius: 50px;
  font-weight: 600;
  
  &:hover {
    background-color: rgba(var(--secondary-color-rgb), 0.1);
    border-color: var(--accent-color);
    color: var(--accent-color);
  }
`;

const ContentContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeading = styled.h3`
  text-align: center;
  margin-bottom: 1.25rem;
  color: var(--secondary-color);
  font-size: 1.5rem;
`;

const LoadingMessage = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.4rem;
  color: var(--accent-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const LoadingSpinner = styled(motion.div)`
  width: 50px;
  height: 50px;
  border: 5px solid rgba(var(--primary-color-rgb), 0.2);
  border-radius: 50%;
  border-top-color: var(--accent-color);
  margin-bottom: 1rem;
`;

const InfoBar = styled.div`
  background-color: rgba(var(--secondary-color-rgb), 0.1);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 2rem;
  text-align: center;
  border-left: 3px solid var(--secondary-color);
  
  p {
    margin: 0;
    font-style: italic;
  }
`;

const ShareContainer = styled.div`
  margin: 2rem 0 1rem;
  text-align: center;
`;

const Footer = styled.footer`
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(var(--primary-color-rgb), 0.1);
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  
  a {
    color: var(--secondary-color);
    opacity: 0.9;
    text-decoration: none;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 1;
      color: var(--accent-color);
      text-decoration: underline;
    }
  }
`;

// Function to generate a new build for the same god
const generateNewBuildForGod = (god) => {
  const starters = getStarters();
  const relics = getRelics();
  const tier3Items = getTier3Items();
  
  const randomStarter = starters[Math.floor(Math.random() * starters.length)];
  const randomRelic = relics[Math.floor(Math.random() * relics.length)];
  
  const randomItems = [];
  // Select 6 unique tier 3 items
  const itemsCopy = [...tier3Items];
  for (let i = 0; i < 6; i++) {
    if (itemsCopy.length === 0) break;
    const randomIndex = Math.floor(Math.random() * itemsCopy.length);
    randomItems.push(itemsCopy[randomIndex]);
    itemsCopy.splice(randomIndex, 1);
  }
  
  return {
    god,
    starter: randomStarter,
    relic: randomRelic,
    items: randomItems
  };
};

const HomePage = () => {
  const [randomBuild, setRandomBuild] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statsUpdated, setStatsUpdated] = useState(0); // Used to trigger re-render of CounterStats
  
  // Load previous build from localStorage on first render
  useEffect(() => {
    const savedBuild = localStorage.getItem('lastSmiteRandomizerBuild');
    if (savedBuild) {
      try {
        setRandomBuild(JSON.parse(savedBuild));
      } catch (e) {
        console.error('Failed to parse saved build');
      }
    }
  }, []);
  
  // Generate entirely new god and build
  const handleNewGodClick = () => {
    setIsLoading(true);
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      const newBuild = generateRandomBuild();
      setRandomBuild(newBuild);
      setIsLoading(false);
      
      // Update counters
      incrementGodRolls();
      incrementBuildRolls();
      setStatsUpdated(prev => prev + 1);
      
      // Save to localStorage
      localStorage.setItem('lastSmiteRandomizerBuild', JSON.stringify(newBuild));
    }, 800);
  };
  
  // Generate new build for the same god
  const handleSameGodNewBuildClick = () => {
    if (!randomBuild || !randomBuild.god) {
      // If there's no current god, just generate a completely new build
      handleNewGodClick();
      return;
    }
    
    setIsLoading(true);
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      const newBuild = generateNewBuildForGod(randomBuild.god);
      setRandomBuild(newBuild);
      setIsLoading(false);
      
      // Update counters
      incrementBuildRolls();
      setStatsUpdated(prev => prev + 1);
      
      // Save to localStorage
      localStorage.setItem('lastSmiteRandomizerBuild', JSON.stringify(newBuild));
    }, 800);
  };
  
  return (
    <Container as="main">
      <Header as="header">
        <HeroSection>
          <HeroHeader>
            <Title
              as="h1"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Smite Randomizer
            </Title>
          </HeroHeader>
          
          <HeroContent>
            <HeroColumn>
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M8.93 6.588l-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                </svg>
                About This Tool
              </SectionTitle>
              <SectionContent>
                <p>
                  Generate a random god and build for Smite. Each build includes a starter item, a relic, and six tier 3 items. Perfect for casual players, competitive gamers trying new gods, and team challenges with friends.
                </p>
              </SectionContent>
            </HeroColumn>
            
            <HeroColumn>
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8.05 9.6c.336 0 .504-.24.554-.627.04-.534.198-.815.847-1.26.673-.475 1.049-1.09 1.049-1.986 0-1.325-.92-2.227-2.262-2.227-1.02 0-1.792.492-2.1 1.29A1.71 1.71 0 0 0 6 5.48c0 .393.203.64.545.64.272 0 .455-.147.564-.51.158-.592.525-.915 1.074-.915.61 0 1.03.446 1.03 1.084 0 .563-.208.885-.822 1.325-.619.433-.926.914-.926 1.64v.111c0 .428.208.745.585.745z"/>
                  <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-1 0A7 7 0 1 0 1 8a7 7 0 0 0 14 0z"/>
                </svg>
                How to Use
              </SectionTitle>
              <SectionContent>
                <StepsList>
                  <Step>Click "New God + Build" for a random god with items</Step>
                  <Step>Click "New Build for [God]" to keep same god with new items</Step>
                  <Step>Click on abilities/items for detailed information</Step>
                  <Step>Your build is saved locally for your next visit</Step>
                </StepsList>
              </SectionContent>
            </HeroColumn>
          </HeroContent>
          
          <ButtonContainer>
            <GenerateButton
              onClick={handleNewGodClick}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              disabled={isLoading}
            >
              New God + Build
              <span role="img" aria-label="sparkle">✨</span>
            </GenerateButton>
            
            {randomBuild && (
              <NewBuildButton
                onClick={handleSameGodNewBuildClick}
                whileTap={{ scale: 0.95 }}
                disabled={isLoading}
              >
                New Build for {randomBuild.god.name}
                <span role="img" aria-label="dice">🎲</span>
              </NewBuildButton>
            )}
          </ButtonContainer>
        </HeroSection>
        
        <CounterStats key={statsUpdated} />
      </Header>
      
      <AnimatePresence mode="wait">
        {isLoading ? (
          <LoadingMessage
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LoadingSpinner 
              animate={{ rotate: 360 }}
              transition={{ duration:.8, repeat: Infinity, ease: "linear" }}
            />
            Generating your random build...
          </LoadingMessage>
        ) : randomBuild ? (
          <ContentContainer
            as="section"
            aria-label="Random Build Result"
            key="content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <SectionHeading>
                Random God
              </SectionHeading>
              <GodCard god={randomBuild.god} />
              {randomBuild.god && (
                <InfoBar>
                  <p>Click "New Build for {randomBuild.god.name}" to try different items with the same god!</p>
                </InfoBar>
              )}
            </div>
            <div>
              <SectionHeading>
                {randomBuild?.god ? `Random Build for ${randomBuild.god.name}` : 'Random Build'}
              </SectionHeading>
              <BuildDisplay build={randomBuild} />
            </div>
          </ContentContainer>
        ) : null}
      </AnimatePresence>
      
      <SocialShare />
      
      <Footer as="footer">
        <p>Smite Randomizer &copy; {new Date().getFullYear()} - Data sourced from Smite2.live • This app is not affiliated with Hi-Rez Studios • Inspired by <a href="https://www.twitch.tv/jclegit" target="_blank" rel="noopener noreferrer">JCLegit</a> • Happy Smiting!</p>
      </Footer>
    </Container>
  );
};

export default HomePage; 